import React, { useState, useEffect } from 'react';
import { dashboardAPI, ordersAPI, customersAPI, deliveriesAPI, invoicesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChartLine,
  TrendUp,
  TrendDown,
  CurrencyGbp,
  Truck,
  Users,
  ShoppingCart,
  Package,
  Drop,
  Clock,
  Target,
  Percent
} from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

const COLORS = ['#f97316', '#0f172a', '#10b981', '#ef4444', '#0ea5e9', '#8b5cf6'];

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7');
  const [stats, setStats] = useState(null);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState({});
  const [customers, setCustomers] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const [statsRes, deliveriesRes, revenueRes, ordersRes, customersRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getDeliveriesByDay(parseInt(timeRange)),
        dashboardAPI.getRevenueByDay(parseInt(timeRange)),
        dashboardAPI.getOrdersSummary(),
        customersAPI.list({ limit: 100 })
      ]);

      setStats(statsRes.data);
      setDeliveriesData(deliveriesRes.data);
      setRevenueData(revenueRes.data);
      setOrdersSummary(ordersRes.data);
      setCustomers(customersRes.data);

      // Calculate top customers by credit exposure (simulating order volume)
      const sorted = [...customersRes.data].sort((a, b) => 
        (b.credit?.current_exposure || 0) - (a.credit?.current_exposure || 0)
      ).slice(0, 5);
      setTopCustomers(sorted);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-GB').format(value || 0);
  };

  // Calculate KPIs
  const totalDeliveries = deliveriesData.reduce((sum, d) => sum + d.total, 0);
  const completedDeliveries = deliveriesData.reduce((sum, d) => sum + d.completed, 0);
  const deliveryRate = totalDeliveries > 0 ? ((completedDeliveries / totalDeliveries) * 100).toFixed(1) : 0;
  const totalRevenue = revenueData.reduce((sum, d) => sum + d.revenue, 0);
  const avgDailyRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;

  // Prepare pie chart data for order status
  const orderStatusData = Object.entries(ordersSummary).map(([status, count]) => ({
    name: status.replace('_', ' '),
    value: count
  }));

  // Prepare customer distribution data
  const customerDistribution = [
    { name: 'Active', value: customers.filter(c => c.is_active).length },
    { name: 'On Stop', value: customers.filter(c => c.credit?.is_on_stop).length },
    { name: 'Inactive', value: customers.filter(c => !c.is_active).length }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ChartLine className="w-7 h-7 text-orange-500" />
            Analytics
          </h1>
          <p className="text-muted-foreground">Business intelligence and performance metrics</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-heading font-bold">{formatCurrency(totalRevenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Avg {formatCurrency(avgDailyRevenue)}/day
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <CurrencyGbp weight="duotone" className="w-5 h-5 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-navy-800">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Deliveries</p>
                <p className="text-2xl font-heading font-bold">{formatNumber(totalDeliveries)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {completedDeliveries} completed
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-navy-500/10 flex items-center justify-center">
                <Truck weight="duotone" className="w-5 h-5 text-navy-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Delivery Rate</p>
                <p className="text-2xl font-heading font-bold">{deliveryRate}%</p>
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <TrendUp className="w-3 h-3" />
                  On-time completion
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Target weight="duotone" className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-sky-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Customers</p>
                <p className="text-2xl font-heading font-bold">{formatNumber(stats?.total_customers || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {customers.filter(c => c.credit?.is_on_stop).length} on credit stop
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <Users weight="duotone" className="w-5 h-5 text-sky-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="revenue">Revenue Trends</TabsTrigger>
          <TabsTrigger value="deliveries">Delivery Performance</TabsTrigger>
          <TabsTrigger value="orders">Order Analysis</TabsTrigger>
          <TabsTrigger value="customers">Customer Insights</TabsTrigger>
        </TabsList>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Revenue Over Time</CardTitle>
                <CardDescription>Daily revenue for the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => format(new Date(value), 'dd MMM')}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `£${(value/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                        formatter={(value) => [formatCurrency(value), 'Revenue']}
                        labelFormatter={(label) => format(new Date(label), 'dd MMM yyyy')}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Revenue Breakdown</CardTitle>
                <CardDescription>By period summary</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Period Revenue</p>
                    <p className="text-3xl font-heading font-bold text-orange-600">{formatCurrency(totalRevenue)}</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Average Daily</p>
                    <p className="text-xl font-bold">{formatCurrency(avgDailyRevenue)}</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Peak Day</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(Math.max(...revenueData.map(d => d.revenue), 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Deliveries Tab */}
        <TabsContent value="deliveries" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Delivery Performance</CardTitle>
                <CardDescription>Total vs completed deliveries</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={deliveriesData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => format(new Date(value), 'dd MMM')}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                        labelFormatter={(label) => format(new Date(label), 'dd MMM yyyy')}
                      />
                      <Legend />
                      <Bar dataKey="total" fill="#0f172a" name="Total" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="completed" fill="#f97316" name="Completed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm">Completion Rate</span>
                    <Badge className="bg-emerald-500 text-white">{deliveryRate}%</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm">Total Scheduled</span>
                    <span className="font-mono font-bold">{totalDeliveries}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm">Completed</span>
                    <span className="font-mono font-bold text-emerald-600">{completedDeliveries}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm">Pending/Failed</span>
                    <span className="font-mono font-bold text-orange-600">{totalDeliveries - completedDeliveries}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm">Active Loads</span>
                    <span className="font-mono font-bold">{stats?.loads_in_progress || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Orders by Status</CardTitle>
                <CardDescription>Distribution of order statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Order Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(ordersSummary).map(([status, count], index) => (
                    <div key={status} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="capitalize">{status.replace('_', ' ')}</span>
                      </div>
                      <span className="font-mono font-bold">{count}</span>
                    </div>
                  ))}
                  <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg mt-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Total Orders</span>
                      <span className="text-2xl font-heading font-bold text-orange-600">
                        {Object.values(ordersSummary).reduce((a, b) => a + b, 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Customer Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={customerDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#ef4444" />
                        <Cell fill="#94a3b8" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Top Customers by Exposure</CardTitle>
                <CardDescription>Highest credit utilization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topCustomers.map((customer, index) => (
                    <div key={customer.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">{customer.company_name}</p>
                          <p className="text-xs text-muted-foreground">{customer.account_number}</p>
                        </div>
                      </div>
                      <span className="font-mono font-bold">
                        {formatCurrency(customer.credit?.current_exposure || 0)}
                      </span>
                    </div>
                  ))}
                  {topCustomers.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No customer data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customer Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-heading font-bold">{customers.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-heading font-bold text-emerald-600">
                  {customers.filter(c => c.is_active && !c.credit?.is_on_stop).length}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">On Credit Stop</p>
                <p className="text-2xl font-heading font-bold text-red-600">
                  {customers.filter(c => c.credit?.is_on_stop).length}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Credit Exposure</p>
                <p className="text-2xl font-heading font-bold text-orange-600">
                  {formatCurrency(customers.reduce((sum, c) => sum + (c.credit?.current_exposure || 0), 0))}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
